import {
  type TrackEvent,
  InfoEvent,
  type AdInfoTrackData,
  type AdInfoSlotPayload,
  type AdInfoClickTrackData,
  type AdInfoClickPayload,
} from "common-types";

import type { Geo } from "../../../../geo";
import { send } from "./sender";
import { getDeviceType } from "../../../../device";
import type { SlotUid } from "../utils/slotUid";
import {
  getArticleId,
  getBeaconId,
  getOptIn,
  getOptOutSale,
  getOSAndBrowser,
  getPageLang,
  getSessionId,
  getTrackingPpid,
  getTrackingPv,
  getTrackingPvGlobal,
  getTrackingPvUid,
  getUserId,
  getWikiId,
  getWikiName,
  getWikiPageType,
  getWikiSkin,
  getWikiVertical,
} from "../utils/beaconParams";

import type { InfoSenderBody } from "../types";
import { getExpBucket } from "utils";

declare let window: Window & {
  OneTrust: unknown;
};

export function createClickBody(
  { event, payload }: AdInfoClickTrackData,
  slotUid: SlotUid,
): InfoSenderBody {
  const nowDate = new Date();
  const geo: Geo = window.fandomHost!.geo!;
  const body: InfoSenderBody = {
    ad_status: getAdStatusEventName(event),
    timestamp: nowDate.getTime(),
    tz_offset: nowDate.getTimezoneOffset(),
    device: getDeviceType(),
    country: geo.country,
    region: geo.region,
    lang: getPageLang(),

    // Omitted from click events. TODO: this appears to be unintentional because
    // AE3 is sending them.
    //advertiser_id: getAdvertiserId(payload),
    //creative_id: getCreativeId(payload),
    //product_lineitem_id: getLineItemId(payload),
    //order_id: getOrderId(payload),

    slot_id: getSlotUid(payload, slotUid),

    // hardcoded because not available on the click payload currently
    slot_size: "",

    kv_pos: payload.slotName,
    kv_rv: getRv(payload),
    kv_s0v: getWikiVertical(),
    kv_s1: `_${getWikiName()}`,
    kv_s2: getWikiPageType(),
    kv_skin: getWikiSkin(),
    ppid: getTrackingPpid(),
    page_width: window.document.body.scrollWidth.toString(),
    viewport_height: defaultZero(window.innerHeight),
    rollout_tracking: getCmpSystemName(),
    opt_in: getOptIn(),
    is_uap: 0, // TODO: add detection after UAP implementation
    opt_out_sale: getOptOutSale(),

    session_id: getSessionId(),
    pv_number: getTrackingPv(),
    pv_number_global: getTrackingPvGlobal(),
    pv_unique_id: getTrackingPvUid(),
    beacon: getBeaconId(),
    c: getWikiId(),
    x: getWikiName(),
    lc: getPageLang(),
    s: getWikiSkin(),
    ua: window.navigator.userAgent,
    u: getUserId(),
    a: getArticleId(),
    n: -1,
    exp_bucket: getExpBucket(),
    browser: getOSAndBrowser(),
    odyssey_version: window.fandomAds?.version ?? "unknown",
  };
  return body;
}

export function createInfoBody(
  { event, payload }: AdInfoTrackData,
  slotUid: SlotUid,
): InfoSenderBody {
  const nowDate = new Date();
  const geo: Geo = window.fandomHost!.geo!;
  const body: InfoSenderBody = {
    ad_status: getAdStatusEventName(event),
    timestamp: nowDate.getTime(),
    tz_offset: nowDate.getTimezoneOffset(),
    device: getDeviceType(),
    country: geo.country,
    region: geo.region,
    lang: getPageLang(),
    advertiser_id: getAdvertiserId(payload),
    creative_id: getCreativeId(payload),
    product_lineitem_id: getLineItemId(payload),
    order_id: getOrderId(payload),
    slot_id: getSlotUid(payload, slotUid),
    slot_size: getSlotSize(payload),
    kv_pos: payload.slotName,
    kv_rv: getRv(payload),
    kv_s0v: getWikiVertical(),
    kv_s1: `_${getWikiName()}`,
    kv_s2: getWikiPageType(),
    kv_skin: getWikiSkin(),
    ppid: getTrackingPpid(),
    page_width: window.document.body.scrollWidth.toString(),
    viewport_height: defaultZero(window.innerHeight),
    rollout_tracking: getCmpSystemName(),
    opt_in: getOptIn(),
    is_uap: 0, // TODO: add detection after UAP implementation
    opt_out_sale: getOptOutSale(),

    session_id: getSessionId(),
    pv_number: getTrackingPv(),
    pv_number_global: getTrackingPvGlobal(),
    pv_unique_id: getTrackingPvUid(),
    beacon: getBeaconId(),
    c: getWikiId(),
    x: getWikiName(),
    lc: getPageLang(),
    s: getWikiSkin(),
    ua: window.navigator.userAgent,
    u: getUserId(),
    a: getArticleId(),
    n: -1,
    exp_bucket: getExpBucket(),
    browser: getOSAndBrowser(),
    odyssey_version: window.fandomAds?.version ?? "unknown",
  };
  return body;
}

export async function sendAdInfo(
  fandomTrackData: AdInfoTrackData | AdInfoClickTrackData,
  slotUid: SlotUid,
): Promise<void> {
  // TODO: My expectation is that we should update the payload of click data to add all the same
  // parameters as the other Info events. Verify with data engineering team if the omitted fields
  // are expected for click events like they are for other events.
  let body;
  if (
    [InfoEvent.SLOT_CLICKED, InfoEvent.SLOT_FORCE_CLOSE_INFO].includes(
      fandomTrackData.event,
    )
  ) {
    body = createClickBody(fandomTrackData as AdInfoClickTrackData, slotUid);
  } else {
    body = createInfoBody(fandomTrackData as AdInfoTrackData, slotUid);
  }

  send("adengadinfo", body);
}

/**
 * ad_status is used to denote which type of event occured, e.g. "requested", "code_served", or "success"
 *
 * Today the `TrackEvent` text string happens to match those event names but with `_info` appended. This
 * function strips that off to produce the event name that Beacon expects.
 * @param event
 */
function getAdStatusEventName(event: TrackEvent) {
  return event.toString().replace("_info", "");
}

function getCmpSystemName(): string {
  return window.OneTrust !== undefined ? "1T" : "toi";
}

function defaultZero(value: number | null | undefined): number {
  return value ?? 0;
}

function getAdvertiserId(payload: AdInfoSlotPayload): string {
  return `${payload.slotInfo?.advertiserId ?? ""}`;
}
function getCreativeId(payload: AdInfoSlotPayload): string {
  return `${payload.slotInfo?.creativeId ?? ""}`;
}
function getLineItemId(payload: AdInfoSlotPayload): string {
  return `${payload.slotInfo?.lineItemId ?? ""}`;
}
function getOrderId(payload: AdInfoSlotPayload): string {
  return `${payload.slotInfo?.orderId ?? ""}`;
}

/**
 * Uses the slotUid mapper utility to fetch the pageload-specific unique randomly
 * generated identifier for the slot.
 *
 * This field is deprecated in favor of using the `pos` key for slotName in conjunction
 * with the pv_unique_id. It is maintained for backwards compatibility during the migration
 * to AE4.
 *
 * TODO: this code maintains previously existing behavior, but it has an edge case if payload.slotName is undefined.
 * This current approach will generate a slot uid for the `"unknown" slot that then
 * is shared across all "unknown" slots. Should rethink this behavior.... this should be an unexpected edge case
 * on a deprecated field so not dealing with it now.
 * @param payload
 * @param slotUid
 * @returns
 */
function getSlotUid(
  payload: AdInfoSlotPayload | AdInfoClickPayload,
  slotUid: SlotUid,
): string {
  const slotName = payload.slotName ?? "unknown";
  return slotUid.getUid(slotName);
}

/**
 * Populates the "slot_size" attribute of the info events.
 * @param payload
 * @returns
 */
function getSlotSize(payload: AdInfoSlotPayload): string {
  if (payload.slotInfo?.selectedSize) {
    return payload.slotInfo.selectedSize;
  }

  if (!payload.slotInfo?.configuredSizes) {
    // TODO check if there is a better value to return here, like "unknown"
    return "";
  }

  // TODO: is this actually the behavior desired? Seems unintuitive.
  return payload.slotInfo.configuredSizes.join(",");
}

function getRv(payload: AdInfoSlotPayload | AdInfoClickPayload): number {
  return payload.rv ?? -1;
}
